// MateQuoteInsightsNudge.jsx — BM-H80 warm-instruction nudge for the
// Bonsai Mate web pane.
//
// Mirrors the Brief 50.2 `MatePaymentRequestsNudge` sub-component:
// 60s poll, dismissible per-user (localStorage), silent no-op on error
// so a hiccup in the /insights endpoint never breaks the Mate UX.
//
// V's decision on scope: team-wide (not per-FE). Every fee earner sees
// the same team backlog and self-selects what to action.
//
// Wire-in: mount alongside the existing MatePaymentRequestsNudge inside
// BonsaiMate.jsx — see HANDOFF.md snippet.

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { X, ChevronDown, ChevronUp, FileText, MailOpen, Edit3 } from "lucide-react";
import { API } from "../config/api";

const POLL_INTERVAL_MS = 60 * 1000;
const DISMISS_KEY = "bmh80_quote_insights_dismissed_until";
const DISMISS_HOURS = 12; // dismiss for 12h at a time

function authHeaders() {
  const uid = sessionStorage.getItem("bonsai_user_id") ||
              localStorage.getItem("bonsai_user_id") || "";
  return { "X-Bonsai-User-Id": uid };
}

function fmtGbp(pounds) {
  const n = Number(pounds);
  if (!Number.isFinite(n)) return "";
  return new Intl.NumberFormat("en-GB", {
    style: "currency", currency: "GBP",
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(n);
}

function isDismissed() {
  try {
    const until = Number(localStorage.getItem(DISMISS_KEY) || "0");
    return Number.isFinite(until) && until > Date.now();
  } catch { return false; }
}

function dismissForNow() {
  try {
    localStorage.setItem(DISMISS_KEY,
      String(Date.now() + DISMISS_HOURS * 60 * 60 * 1000));
  } catch { /* nosilent:bmh80_ls_write — sandbox / disabled storage */ }
}

function Section({ title, kind, rows, icon: Icon }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div data-testid={`mate-quote-insights-section-${kind}`} className="mt-2">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3 h-3 text-status-amber" />
        <span className="text-[11px] font-medium text-status-amber uppercase tracking-wider">
          {title} — {rows.length}
        </span>
      </div>
      <ul className="space-y-0.5">
        {rows.slice(0, 5).map((r) => (
          <li key={r.quote_ref} className="text-xs text-gray-300 flex items-center justify-between gap-2"
            data-testid={`mate-quote-insights-row-${r.quote_ref}`}>
            <a href={`/quotes/${r.quote_ref}`}
              className="text-teal hover:underline flex-1 min-w-0 truncate"
              data-testid={`mate-quote-insights-link-${r.quote_ref}`}>
              {r.quote_ref}
              <span className="text-gray-500"> · {r.client_name || r.client_email || ""}</span>
            </a>
            <span className="text-gray-500 whitespace-nowrap">
              {r.days_since}d{r.grand_total ? ` · ${fmtGbp(r.grand_total)}` : ""}
            </span>
          </li>
        ))}
        {rows.length > 5 && (
          <li className="text-xs text-gray-500 italic pl-4">
            and {rows.length - 5} more —{" "}
            <a href="/quotes" className="text-teal hover:underline">open the tracker</a>
          </li>
        )}
      </ul>
    </div>
  );
}

export default function MateQuoteInsightsNudge() {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(isDismissed());
  const [expanded, setExpanded] = useState(false);
  const pollRef = useRef(null);

  const fetchInsights = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/hub/quotes/insights`, { headers: authHeaders() });
      setData(r.data || null);
    } catch {
      // Silent — V's guardrail on the Brief 50.2 nudge: a poll hiccup must
      // never render an error state inside Mate.
      setData(null);
    }
  }, []);

  useEffect(() => {
    if (dismissed) return undefined;
    fetchInsights();
    pollRef.current = setInterval(fetchInsights, POLL_INTERVAL_MS);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [dismissed, fetchInsights]);

  const totalCount = data?.counts?.total || 0;
  const summary = useMemo(() => {
    if (!data) return "";
    const c = data.counts || {};
    const parts = [];
    if (c.accepted)    parts.push(`${c.accepted} accepted`);
    if (c.sent_opened) parts.push(`${c.sent_opened} opened-no-reply`);
    if (c.draft)       parts.push(`${c.draft} draft${c.draft === 1 ? "" : "s"}`);
    return parts.join(" · ");
  }, [data]);

  if (dismissed || !data || totalCount === 0) return null;

  return (
    <div
      data-testid="mate-quote-insights-nudge"
      className="bg-status-amber/10 border border-status-amber/30 rounded-md p-3 mb-3">
      <div className="flex items-start justify-between gap-2">
        <button
          type="button"
          onClick={() => setExpanded(e => !e)}
          data-testid="mate-quote-insights-toggle"
          className="flex-1 text-left min-w-0">
          <div className="flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5 text-status-amber" />
            <span className="text-xs font-medium text-status-amber"
              data-testid="mate-quote-insights-headline">
              {totalCount} warm quote{totalCount === 1 ? "" : "s"} needing attention
            </span>
            {expanded
              ? <ChevronUp className="w-3 h-3 text-status-amber ml-auto" />
              : <ChevronDown className="w-3 h-3 text-status-amber ml-auto" />}
          </div>
          {summary && (
            <div className="text-[11px] text-gray-400 mt-0.5"
              data-testid="mate-quote-insights-summary">
              {summary}
            </div>
          )}
        </button>
        <button
          type="button"
          onClick={() => { dismissForNow(); setDismissed(true); }}
          data-testid="mate-quote-insights-dismiss"
          className="text-gray-500 hover:text-gray-300 shrink-0"
          aria-label="Dismiss for 12 hours">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {expanded && (
        <div data-testid="mate-quote-insights-expanded" className="pt-1">
          <Section title="Accepted — no matter opened"
            kind="accepted" icon={FileText} rows={data.accepted_stalled} />
          <Section title="Client opened — no reply"
            kind="sent_opened" icon={MailOpen} rows={data.sent_opened_stalled} />
          <Section title="Drafts idle"
            kind="draft" icon={Edit3} rows={data.draft_stalled} />
          <div className="mt-2 pt-2 border-t border-status-amber/20">
            <a
              href="/quotes"
              data-testid="mate-quote-insights-open-tracker"
              className="text-xs text-teal hover:underline">
              Open Quotes tracker &rarr;
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
